"use client";
import { PreviewMessage, ThinkingMessage } from "./message";
import { Greeting } from "./greeting";
import { memo, useEffect, useMemo } from "react";
import equal from "fast-deep-equal";
import { useMessages } from "@/hooks/use-messages";
import { useDataStream } from "./data-stream-provider";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { usePathname } from "next/navigation";
import { ExploreBanner } from "./explore-banner";

function PureMessages({
  chatId,
  status,
  messages,
  setMessages,
  regenerate,
  isReadonly,
  showCode,
  // userId,
  isArtifactVisible,
}) {
  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    onViewportEnter,
    onViewportLeave,
    hasSentMessage,
  } = useMessages({ chatId, status });
  const { activeTab } = useSelector((state) => state.main);
  const pathname = usePathname();
  const isHome = pathname === "/";
  const effectiveTab = isHome ? activeTab ?? "Page Builder" : activeTab;
  const shouldShowBanner = isHome;
  const bannerCollapsed = messages.length > 0;
  const lastMessageSignature = useMemo(() => {
    const last = messages?.[messages.length - 1];
    if (!last) return "empty";
    const partsSig = (last.parts || [])
      .map((part) =>
        part?.type === "text"
          ? `t:${(part.text || "").length}`
          : part?.type === "data-html"
            ? `h:${(part.data || "").length}`
            : part?.type || "x"
      )
      .join("|");
    return `${last.id || "no-id"}:${last.role || "no-role"}:${partsSig}`;
  }, [messages]);

  useEffect(() => {
    const getScrollContainer = () => {
      if (typeof document === "undefined") return null;
      return (
        messagesEndRef.current?.closest("[data-chat-scroll-container]") ||
        document.scrollingElement ||
        document.documentElement
      );
    };

    const scrollBottom = (behavior = "auto") => {
      const container = getScrollContainer();
      if (container && typeof container.scrollTo === "function") {
        if (behavior === "smooth") {
          container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
    };

    // Run multiple passes to handle async rendering/stream updates.
    requestAnimationFrame(() => scrollBottom("auto"));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollBottom("smooth"));
    });
    const t1 = setTimeout(() => scrollBottom("auto"), 80);
    const t2 = setTimeout(() => scrollBottom("smooth"), 180);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chatId, status, messages.length, lastMessageSignature]);

  useDataStream();
  return (
    <div
      ref={messagesContainerRef}
      className="pb-0 md:pb-0 w-full"
    >
      {/* {shouldShowBanner && (
        <ExploreBanner
          type={effectiveTab ?? "Page Builder"}
          collapsed={bannerCollapsed}
        />
      )} */}
      {isHome && messages.length === 0 && <Greeting type={effectiveTab} />}


      {messages.map((message, index) => {
        let isLast = messages.length - 1 == index
        return (
          <PreviewMessage
            chatId={chatId}
            key={`${message.id || "msg"}-${index}`}
            message={message}
            isLoading={false}
            setMessages={setMessages}
            regenerate={regenerate}
            isReadonly={isReadonly}
            requiresScrollPadding={
              false
            }
            showCode={showCode}
            isLast={isLast}
            index={index}
          />
        )
      })}

      {status === "submitted" &&
        messages.length > 0 &&
        messages[messages.length - 1].role === "user" && <ThinkingMessage />}
      <div ref={messagesEndRef} />
    </div>
  );
}

export const Messages = memo(PureMessages, (prevProps, nextProps) => {
  if (prevProps.isArtifactVisible && nextProps.isArtifactVisible) return true;
  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.messages.length !== nextProps.messages.length) return false;
  if (!equal(prevProps.messages, nextProps.messages)) return false;
  if (prevProps.showCode !== nextProps.showCode) return false;

  return false;
});
